<script>
import { copy } from '@utils/Clipboard'

export default {
  name: 'AssetsAnalysisTable',
  props: {
    assets: { type: Object, required: true },
    groupAssets: { type: Boolean, required: true },
    loadingAssets: { type: Boolean, required: true },
    classBenchmarkMap: { type: Object, required: true },
    dataReferenceEnd: { type: String, default: '' },
    compactThreshold: { type: Number, default: 500 },
    compactSearchMinLength: { type: Number, default: 3 },
    normalSearchMinLength: { type: Number, default: 1 },
    hideGroupAssetsToggle: { type: Boolean, default: false },
    showOwnerColumn: { type: Boolean, default: false },
  },
  data: () => ({
    displayGroups: [],
    openMap: {},
    searchQuery: '',
    showOldAssets: false,
  }),
  computed: {
    hasAssets() {
      return !!Object.keys(this.assets || {}).length
    },
    groupAssetsProxy: {
      get() {
        return this.groupAssets
      },
      set(value) {
        this.$emit('update:groupAssets', value)
      },
    },
    visibleGroups() {
      const groups = this.displayGroups || []
      if (!this.hasActiveSearch) return groups
      const query = (this.searchQuery || '').trim().toLowerCase()
      const matchedGroups = []
      for (let i = 0; i < groups.length; i++) {
        const name = groups[i] && groups[i][0]
        const group = (groups[i] && groups[i][1]) || {}
        const assets = Array.isArray(group.assets) ? group.assets : []
        const match = assets.filter((asset) =>
          ((asset && asset.nome_ativo) || '').toLowerCase().includes(query)
        )
        if (match.length > 0)
          matchedGroups.push([name, { ...group, assets: match }])
      }
      return matchedGroups
    },
    noResultsColspan() {
      let total = this.showToggleColumn ? 17 : 16
      if (this.hasAssetActionsSlot) {
        total += 1
      }
      if (this.showOwnerColumn) {
        total += 1
      }
      return total
    },
    showToggleColumn() {
      if (!this.isCompactMode || this.hasActiveSearch) return false
      const groups = this.filteredGroups || []
      for (let i = 0; i < groups.length; i++) {
        const name = groups[i] && groups[i][0]
        if (name && name !== 'total_pl') return true
      }
      return false
    },
    hasActiveSearch() {
      const query = (this.searchQuery || '').trim()
      const minLength = this.isCompactMode
        ? this.compactSearchMinLength
        : this.normalSearchMinLength
      return query.length >= minLength
    },
    isCompactMode() {
      const threshold =
        typeof this.compactThreshold === 'number' ? this.compactThreshold : 500
      return (this.totalAssetCount || 0) > threshold
    },
    totalAssetCount() {
      const groups = this.filteredGroups || []
      let count = 0
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i][1] || {}
        const groupAssets = Array.isArray(group.assets) ? group.assets : []
        count += groupAssets.length
      }
      return count
    },
    filteredGroups() {
      const entries = Object.entries(this.displayAssets || {})
      return entries.filter((groupEntry) => groupEntry[0] !== 'balance')
    },
    displayAssets() {
      const source = this.assets || {}
      if (this.showOldAssets) return source
      const result = {}
      for (const key of Object.keys(source)) {
        const group = source[key]
        if (!group || !Array.isArray(group.assets)) {
          result[key] = group
          continue
        }
        const assets = group.assets.filter((asset) => asset && !asset._resgatado)
        if (assets.length === 0) continue
        result[key] = { ...group, assets }
      }
      return result
    },
    hasAssetActionsSlot() {
      return Boolean(this.$scopedSlots.assetActions)
    },
  },
  watch: {
    assets: {
      immediate: true,
      handler() {
        this.resetVisibleAssets()
      },
    },
    searchQuery(newVal) {
      const minLength = this.isCompactMode
        ? this.compactSearchMinLength
        : this.normalSearchMinLength
      const active = (newVal || '').trim().length >= minLength
      if (!active) this.clearOpenGroups()
    },
    showOldAssets() {
      this.resetVisibleAssets()
    },
  },
  mounted() {
    this.resetVisibleAssets()
  },
  methods: {
    toggleGroupOpen(name) {
      if (!this.canToggleGroup(name)) return
      const isOpen = !this.openMap[name]
      this.$set(this.openMap, name, isOpen)
    },
    getAssetsForGroup(groupEntry) {
      const name = groupEntry && groupEntry[0]
      const group = (groupEntry && groupEntry[1]) || {}
      const assets = Array.isArray(group.assets) ? group.assets : []
      if (!this.isCompactMode || this.hasActiveSearch) return assets
      return this.isGroupOpen(name) ? assets : []
    },
    iconForGroup(name) {
      return this.isGroupOpen(name) ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },
    canToggleGroup(name) {
      return !!(
        this.isCompactMode &&
        !this.hasActiveSearch &&
        name !== 'total_pl'
      )
    },
    isGroupOpen(name) {
      if (!this.isCompactMode || this.hasActiveSearch) return true
      return !!this.openMap[name]
    },
    copyQuantity(asset) {
      if (!asset || !asset.has_quantity) return
      return copy(asset.quantidade_raw)
        .then(() =>
          this.$showSnack({
            text: 'Quantidade copiada',
            button: true,
            color: 'success',
          })
        )
        .catch(() =>
          this.$showSnack({
            text: 'Não foi possível copiar a quantidade. Tente novamente.',
            button: true,
            color: 'error',
          })
        )
    },
    benchmarkLabel(groupName) {
      const IBOVESPA_SUFFIX = 'ESPA'
      const benchmark = (this.classBenchmarkMap[groupName] || '').trim()
      if (benchmark === '' || benchmark === 'CDI') return '% CDI'
      if (this.isCustomBenchmark(benchmark)) return benchmark
      const shortName = benchmark.replace(IBOVESPA_SUFFIX, '')
      return shortName + '+%'
    },
    isCustomBenchmark(benchmark) {
      return /[\s+%]/.test(benchmark)
    },
    emitReload() {
      this.$emit('reload-assets')
    },
    resetVisibleAssets() {
      this.openMap = {}
      this.recomputeDisplayGroups()
    },
    clearOpenGroups() {
      this.openMap = {}
    },
    recomputeDisplayGroups() {
      this.displayGroups = this.normalizeGroups()
    },
    normalizeGroups() {
      return Array.isArray(this.filteredGroups) ? this.filteredGroups : []
    },
  },
}
</script>

<template>
  <div v-if="hasAssets" class="assets-analysis-table az-card-bordered mt-5">
    <div class="d-flex justify-content-between">
      <div class="az-ds-card-title">Ativos</div>

      <div class="d-flex align-items-center az-gap">
        <div
          v-if="!hideGroupAssetsToggle"
          class="assets-analysis-table__switch"
        >
          <v-switch
            v-model="groupAssetsProxy"
            dense
            hide-details
            label="Agrupar ativos"
            :loading="loadingAssets"
            @change="emitReload"
          />
        </div>

        <v-checkbox
          v-model="showOldAssets"
          label="Apresentar ativos resgatados"
          hide-details
          class="assets-analysis-table__old-assets m-0 p-0"
        />

        <div class="assets-analysis-table__search">
          <v-text-field
            v-model="searchQuery"
            hide-details
            class="assets-analysis-table__search-input new-ux no-border"
            filled
            placeholder="Pesquisar ativo"
            append-icon="mdi-magnify"
          />
        </div>
      </div>
    </div>

    <div class="assets-analysis-table__table-container mb-5">
      <table id="tabela-ativos-analise" class="assets-analysis-table__table">
        <thead>
          <tr>
            <th class="assets-analysis-table__name-header"></th>
            <th v-if="showOwnerColumn" class="text-left">Membro</th>
            <th>Saldo</th>
            <th>% Carteira</th>
            <th>% Classe</th>
            <th>Qtd.</th>
            <th class="assets-analysis-table__refpl-header">Ref. PL</th>
            <th
              class="assets-analysis-table__rent-month-header text-dark text-left"
              colspan="2"
            >
              Rent Mês
            </th>
            <th
              class="assets-analysis-table__rent-year-header text-dark text-left"
              colspan="2"
            >
              Rent Ano
            </th>
            <th
              class="assets-analysis-table__rent-12m-header text-dark text-left"
              colspan="2"
            >
              Rent 12 meses
            </th>
            <th class="text-dark text-left" colspan="3">Rent início</th>
            <th class="assets-analysis-table__refcot-header">Ref. Cotização</th>
            <th
              v-if="hasAssetActionsSlot"
              class="assets-analysis-table__last-header"
            >
              Objetivo
            </th>
            <th
              v-if="showToggleColumn"
              class="assets-analysis-table__toggle-header"
            ></th>
          </tr>
        </thead>

        <tbody v-if="hasActiveSearch && visibleGroups.length === 0">
          <tr class="assets-analysis-table__no-results az-text-primary az-size-14 az-muted">
            <td
              class="assets-analysis-table__no-results-column text-center"
              :colspan="noResultsColspan"
            >
              Nenhum ativo encontrado para a sua pesquisa.
            </td>
          </tr>
        </tbody>

        <tbody
          v-for="groupEntry in visibleGroups"
          v-else
          :key="`group-${groupEntry[0]}`"
        >
          <tr
            class="assets-analysis-table__grupo"
            :class="{ 'mouse-hover': canToggleGroup(groupEntry[0]) }"
            @click="toggleGroupOpen(groupEntry[0])"
          >
            <td class="assets-analysis-table__name-group text-left">
              <div class="assets-analysis-table__group-left">
                <strong>
                  {{
                    groupEntry[0] === 'total_pl' ? 'P/L Atual' : groupEntry[0]
                  }}
                </strong>
              </div>
            </td>

            <td v-if="showOwnerColumn"></td>

            <td class="text-right">
              <strong>
                {{
                  groupEntry[0] === 'total_pl'
                    ? groupEntry[1]
                    : groupEntry[1].pl || '-'
                }}
              </strong>
            </td>

            <td class="text-right">
              <strong>{{ groupEntry[1].weight || '-' }}</strong>
            </td>

            <td class="text-center"></td>

            <td></td>

            <td class="assets-analysis-table__refpl-group"></td>

            <td class="text-center">
              <strong v-if="groupEntry[0] !== 'total_pl'">%</strong>
            </td>
            <td class="assets-analysis-table__rent-month-group">
              <strong v-if="groupEntry[0] !== 'total_pl'">
                {{ benchmarkLabel(groupEntry[0]) }}
              </strong>
            </td>

            <td class="text-center">
              <strong v-if="groupEntry[0] !== 'total_pl'">%</strong>
            </td>
            <td class="assets-analysis-table__rent-year-group">
              <strong v-if="groupEntry[0] !== 'total_pl'">
                {{ benchmarkLabel(groupEntry[0]) }}
              </strong>
            </td>

            <td class="text-center">
              <strong v-if="groupEntry[0] !== 'total_pl'">%</strong>
            </td>
            <td class="assets-analysis-table__rent-12m-group">
              <strong v-if="groupEntry[0] !== 'total_pl'">
                {{ benchmarkLabel(groupEntry[0]) }}
              </strong>
            </td>

            <td class="text-center">
              <strong v-if="groupEntry[0] !== 'total_pl'">Data</strong>
            </td>
            <td class="text-center">
              <strong v-if="groupEntry[0] !== 'total_pl'">%</strong>
            </td>
            <td>
              <strong v-if="groupEntry[0] !== 'total_pl'">
                {{ benchmarkLabel(groupEntry[0]) }}
              </strong>
            </td>

            <td class="assets-analysis-table__refcot-group"></td>

            <td v-if="hasAssetActionsSlot" class="text-center"></td>

            <td v-if="showToggleColumn" class="text-center">
              <icon-svg
                v-if="canToggleGroup(groupEntry[0])"
                :name="iconForGroup(groupEntry[0])"
                :size="16"
                color="var(--secondary)"
                role="button"
                tabindex="0"
                aria-label="Expandir ou recolher grupo"
                class="assets-analysis-table__toggle-icon"
                @click.stop="toggleGroupOpen(groupEntry[0])"
                @keydown.enter.stop="toggleGroupOpen(groupEntry[0])"
                @keydown.space.stop.prevent="toggleGroupOpen(groupEntry[0])"
              />
            </td>
          </tr>

          <tr
            v-for="asset in getAssetsForGroup(groupEntry)"
            :key="`asset-${groupEntry[0]}-${asset.id}`"
          >
            <td class="assets-analysis-table__name-item text-left">
              <div>
                <div class="text-truncate">
                  <span :title="asset.nome_ativo || '-'">
                    {{ asset.nome_ativo || '-' }}
                  </span>
                </div>
              </div>
            </td>

            <td v-if="showOwnerColumn" class="text-left">
              {{ asset.membro || '-' }}
            </td>

            <td class="text-right">{{ asset.pl || '-' }}</td>
            <td class="text-right">{{ asset.weight_total || '-' }}</td>
            <td class="text-right">{{ asset.weight || '-' }}</td>

            <td class="text-right">
              <div
                v-if="asset.has_quantity"
                class="assets-analysis-table__quantity"
                role="button"
                tabindex="0"
                :title="asset.quantidade_full"
                :aria-label="'Copiar quantidade ' + asset.quantidade_full"
                @click="copyQuantity(asset)"
                @keydown.enter="copyQuantity(asset)"
                @keydown.space.prevent="copyQuantity(asset)"
              >
                {{ asset.quantidade_display }}
              </div>
            </td>

            <td class="assets-analysis-table__refpl-item text-center">
              {{ asset._d_referencia_pl || '-' }}
            </td>

            <td class="text-center">
              {{ (asset.summary && asset.summary.mes) || '-' }}
            </td>
            <td class="assets-analysis-table__rent-month-item text-center">
              {{ (asset.summary && asset.summary.mes_relativo) || '-' }}
            </td>

            <td class="text-center">
              {{ (asset.summary && asset.summary.ano) || '-' }}
            </td>
            <td class="assets-analysis-table__rent-year-item text-center">
              {{ (asset.summary && asset.summary.ano_relativo) || '-' }}
            </td>

            <td class="text-center">
              {{ (asset.summary && asset.summary['12M']) || '-' }}
            </td>
            <td class="assets-analysis-table__rent-12m-item text-center">
              {{ (asset.summary && asset.summary['12M_relativo']) || '-' }}
            </td>

            <td class="text-center">
              {{ (asset.summary && asset.summary.data_inicio) || '-' }}
            </td>
            <td class="text-center">
              {{ (asset.summary && asset.summary.inicio) || '-' }}
            </td>
            <td class="text-center">
              {{ (asset.summary && asset.summary.inicio_relativo) || '-' }}
            </td>

            <td class="assets-analysis-table__refcot-item text-center">
              {{ asset._d_referencia_cotizacao || '-' }}
            </td>

            <td
              v-if="hasAssetActionsSlot"
              class="assets-analysis-table__actions"
            >
              <slot name="assetActions" :asset="asset" />
            </td>

            <td v-if="showToggleColumn"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="dataReferenceEnd" class="mt-5 az-text-small">
      * Data base para os ativos: {{ dataReferenceEnd }}
    </p>
  </div>
</template>

<style lang="sass">
$border-table-new-ux: 1px solid var(--border-hairline) !important

.assets-analysis-table
  &__switch
    padding-bottom: 16px
    .v-input--selection-controls
      margin: 0px !important

  &__old-assets
    margin-top: 0
    padding-top: 0
    .v-input--selection-controls
      margin: 0 !important

  &__search
    margin-left: auto

  &__search-input
    width: 200px

  &__group-left
    display: flex
    align-items: center
    gap: 6px

  &__actions
    min-width: 180px

  &__quantity
    display: inline-block
    cursor: pointer

    &:hover,
    &:focus-visible
      text-decoration: underline

  &__table-container
    width: 100%
    overflow-x: auto
    max-height: none
    overflow-y: visible

  &__table
    width: 100%
    white-space: nowrap
    border-collapse: collapse

    thead tr th
      font-size: 10px !important
      font-weight: normal !important
      color: var(--neutral-700) !important
      white-space: nowrap !important
      border-bottom: none !important
      background: white !important

    tbody
      font-size: 12px !important
      font-weight: normal !important
      color: var(--neutral-700) !important
      white-space: nowrap !important
      border-bottom: none !important

    th
      padding: 10px 10px 10px 10px
      border-bottom: none !important
      font-size: 0.75rem !important

    td
      padding: 8px 10px
      min-width: 50px

    tbody tr:hover
      background-color: var(--gray-3) !important

  &__grupo
    background: #F4F4F480 !important
    height: 50px
    border-bottom: $border-table-new-ux

    td
      padding-top: 8px

  &__toggle-icon
    color: var(--secondary) !important
    cursor: pointer

  &__name-header, &__name-group, &__name-item
    border-right: $border-table-new-ux

  &__name-group
    width: 25vw

  &__name-item
    max-width: 200px
    height: 35px
    padding-left: 24px !important

  &__refpl-header, &__refpl-group, &__refpl-item
    border-right: $border-table-new-ux
    min-width: 40px
    white-space: nowrap

  &__refcot-header, &__refcot-group, &__refcot-item
    min-width: 40px
    white-space: nowrap

  &__rent-month-header, &__rent-month-group, &__rent-month-item
    border-right: $border-table-new-ux

  &__rent-year-header, &__rent-year-group, &__rent-year-item
    border-right: $border-table-new-ux

  &__rent-12m-header, &__rent-12m-group, &__rent-12m-item
    border-right: $border-table-new-ux

  &__last-header
    min-width: 40px

  &__toggle-header
    width: 36px

  &__no-results
    border:
      top: $border-table-new-ux
      bottom: $border-table-new-ux

    &-column
      padding: 16px 10px
      background: var(--neutral-50)
</style>
